import React, { useState } from 'react';
import { staffAPI, PRESET_SHIFTS, GENDER_OPTIONS } from '../../api/staff';
import UserAvatar from '../UserAvatar';
import { MapPin, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function DistributionTab({ eventId, assignments, onRefresh }) {
  const [view, setView] = useState('shift');

  // TAREA 19: Group by shift - handle multiple shifts per assignment
  const byShift = {};
  assignments.forEach(a => {
    // Get all shifts for this assignment (support both `shifts` array and legacy `shift` object)
    const shifts = Array.isArray(a.shifts) && a.shifts.length > 0
      ? a.shifts
      : a.shift
        ? [a.shift]
        : [];

    if (shifts.length === 0) {
      // No shifts assigned
      const key = '__none__';
      if (!byShift[key]) byShift[key] = { label: 'Sin turno asignado', shift: null, items: [] };
      byShift[key].items.push(a);
    } else {
      // Add assignment to EACH of its shifts
      shifts.forEach(shift => {
        const key = `${shift.name || ''} ${shift.start_time || ''}-${shift.end_time || ''}`.trim() || '__none__';
        if (!byShift[key]) byShift[key] = { label: key === '__none__' ? 'Sin turno asignado' : key, shift, items: [] };
        byShift[key].items.push(a);
      });
    }
  });

  // Group by gender (uses staff object or falls back)
  const byGender = { masculino: [], femenino: [], otro: [], no_declara: [], unknown: [] };
  assignments.forEach(a => {
    const g = a.staff?.gender || 'unknown';
    if (byGender[g]) byGender[g].push(a);
    else byGender.unknown.push(a);
  });

  // Group by location
  const byLocation = {};
  assignments.forEach(a => {
    const key = a.location_in_event || '__none__';
    if (!byLocation[key]) byLocation[key] = { label: key === '__none__' ? 'Sin ubicación' : key, items: [] };
    byLocation[key].items.push(a);
  });

  const PersonChip = ({ assignment }) => {
    const avatarUser = { id: assignment.staff_member_id, name: assignment.staff?.full_name || '—' };
    return (
      <div className="flex items-center gap-2 py-2 px-3 border-b border-slate-50 last:border-0">
        <UserAvatar user={avatarUser} size="xs" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-soem-navy truncate">{assignment.staff?.full_name || '—'}</p>
          {assignment.location_in_event && (
            <p className="text-xs text-slate-400 truncate">{assignment.location_in_event}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* View toggle */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-sm p-1 w-fit">
        {[
          { id: 'shift', label: '👥 Por Turno', icon: Clock },
          { id: 'gender', label: '⚥ Por Género', icon: Users },
          { id: 'location', label: '📍 Por Ubicación', icon: MapPin },
        ].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`px-4 py-1.5 rounded-sm text-xs font-semibold transition-colors ${view === v.id ? 'bg-white text-soem-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {v.label}
          </button>
        ))}
      </div>

      {/* By Shift */}
      {view === 'shift' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(byShift).map(([key, group]) => (
            <div key={key} className={`soem-card overflow-hidden ${key === '__none__' ? 'border-slate-200' : 'border-t-4 border-t-soem-primary'}`}>
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2">
                  <Clock size={14} className={key === '__none__' ? 'text-slate-400' : 'text-soem-primary'} />
                  <p className="font-bold text-soem-navy text-sm">{group.label}</p>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{group.items.length} persona(s)</p>
              </div>
              <div className="max-h-52 overflow-y-auto">
                {group.items.map(a => <PersonChip key={a.id} assignment={a} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* By Gender */}
      {view === 'gender' && (
        <div className="space-y-4">
          {/* Mini donut chart (CSS) */}
          <div className="soem-card p-4">
            <p className="soem-label mb-3">Distribución por género</p>
            <div className="flex items-center gap-6">
              {Object.entries(byGender).filter(([k, v]) => v.length > 0 && k !== 'unknown').map(([k, v]) => {
                const g = GENDER_OPTIONS[k] || { label: k, emoji: '—' };
                const pct = Math.round((v.length / assignments.length) * 100);
                return (
                  <div key={k} className="flex items-center gap-2 text-sm">
                    <span className="text-lg">{g.emoji}</span>
                    <div>
                      <p className="font-bold text-soem-navy">{v.length}</p>
                      <p className="text-xs text-slate-400">{g.label} ({pct}%)</p>
                    </div>
                  </div>
                );
              })}
              {byGender.unknown.length > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-lg">—</span>
                  <div>
                    <p className="font-bold text-soem-navy">{byGender.unknown.length}</p>
                    <p className="text-xs text-slate-400">No especificado</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(byGender).filter(([, v]) => v.length > 0).map(([k, persons]) => {
              const g = GENDER_OPTIONS[k] || { label: 'No especificado', emoji: '—' };
              return (
                <div key={k} className="soem-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                    <p className="font-bold text-soem-navy text-sm">{g.emoji} {g.label}</p>
                    <p className="text-xs text-slate-400">{persons.length} persona(s)</p>
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {persons.map(a => <PersonChip key={a.id} assignment={a} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* By Location */}
      {view === 'location' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(byLocation).map(([key, group]) => (
            <div key={key} className={`soem-card overflow-hidden ${key === '__none__' ? 'border-slate-200' : 'border-t-4 border-t-soem-warning'}`}>
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className={key === '__none__' ? 'text-slate-400' : 'text-orange-500'} />
                  <p className="font-bold text-soem-navy text-sm">{group.label}</p>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{group.items.length} persona(s)</p>
              </div>
              <div className="max-h-52 overflow-y-auto">
                {group.items.map(a => <PersonChip key={a.id} assignment={a} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
